import { useCallback, useState } from 'react'

import {
  browserSupportsWebAuthn,
  platformAuthenticatorIsAvailable,
  PublicKeyCredentialRequestOptionsJSON,
} from '@simplewebauthn/browser'
import { toast } from 'sonner'

import { SplitsDialogContent } from '.'
import { useServerRequests } from '~/hooks/useRequests'
import { captureException } from '~/utils/errors'
import { startPasskeyAuthentication } from '~/utils/passkeys'

export const PasskeyConfirmationDialog = ({
  id,
  title,
  confirmText,
  confirmDisabled,
  confirmDisabledReason,
  children,
  onConfirmed,
  onClose,
}: {
  id: string
  title: string
  confirmText: string
  confirmDisabled?: boolean
  confirmDisabledReason?: string
  children?: React.ReactNode
  onConfirmed: () => Promise<void>
  onClose?: () => void
}) => {
  const { serverGetRequest, serverPostRequest } = useServerRequests()
  const [isLoading, setIsLoading] = useState(false)

  const handleConfirm = useCallback(async () => {
    if (
      !browserSupportsWebAuthn() ||
      !(await platformAuthenticatorIsAvailable())
    ) {
      toast.error('Browser does not support passkeys', {
        description: 'Use a different browser or device to sign with a passkey',
      })
      return
    }

    setIsLoading(true)
    try {
      const options = await serverGetRequest<{
        options: PublicKeyCredentialRequestOptionsJSON
      }>({
        path: '/api/passkey/confirm/options',
      })

      if (!options) return

      const attResp = await startPasskeyAuthentication(options.data.options, {
        onError: ({ error, isNotAllowedError, isUnsupportedError }) => {
          if (isUnsupportedError) {
            toast.error('Browser does not support passkeys', {
              description:
                error instanceof Error ? error.message : String(error),
            })
          } else if (isNotAllowedError) {
            toast.error('Passkey verification cancelled')
          } else {
            toast.error('Failed to verify passkey')
            captureException(error)
          }
        },
      })

      if (!attResp) return

      const verifyResponse = await serverPostRequest<{
        lastVerifiedAt: string
      }>({
        path: '/api/passkey/confirm/verify',
        params: {
          credentialId: attResp.id,
          authentication: JSON.stringify(attResp),
        },
        errorMessage: 'Failed to verify passkey',
      })

      if (!verifyResponse) return

      await onConfirmed()
      onClose?.()
    } catch (error) {
      captureException(error)
    } finally {
      setIsLoading(false)
    }
  }, [serverGetRequest, serverPostRequest, onConfirmed, onClose])

  return (
    <SplitsDialogContent
      id={id}
      title={title}
      variant="confirmation"
      isLoading={isLoading}
      confirmButton={{
        text: confirmText,
        onClick: handleConfirm,
        isDisabled: confirmDisabled,
        disabledReason: confirmDisabledReason,
      }}
    >
      {children ?? <p>You will need to confirm with your passkey to proceed</p>}
    </SplitsDialogContent>
  )
}
